import {
  InputCheckboxGroup,
  InputCheckbox,
  InputTextField
} from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import Stack from '@mui/material/Stack';
import ControlState from '../../Shared/controlState';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import formatName from '../formatName';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ExpressionBuilder from './ExpressionBuilder';
import { useFormContext } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import getNumericFieldIds from '../../Shared/jsonSchemaUtilities/getNumericFieldIds';
import getControlsFromWorkflow from '../../Shared/jsonSchemaUtilities/getControlsFromWorkflow';
import ControlKind from '../../Shared/controlKind';
import RulesEngine from '../Shared/RulesEngine';
import getDefaultRuleConfig from '../Shared/RulesEngine/getDefaultRuleConfig';
import { ConditionalRuleConfig } from '../Shared/RulesEngine/types';
import { useDebounce } from 'usehooks-ts';
import { MathJax } from 'better-react-mathjax';
import SizingSelect from '../../Shared/SizingSelect';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { isReadOnlyView, pages } = state;

  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const validExpressionFieldName = 'validExpression';
  const expressionFieldName = formatName(
    properties['expression']?.$id as string
  );
  const utilizeHiddenComponentsFieldName = formatName(
    properties['utilizeHiddenComponents']?.$id as string
  );
  const rulesFieldName = formatName(properties['rules']?.$id as string);

  const formContext = useFormContext();

  const expressionFieldRef = useRef<HTMLInputElement | null>(null);

  const replaceCurlyBracesByParenthesis = (value: string) => {
    return value.replace(/{/g, '(').replace(/}/g, ')');
  };

  const [expressionValue, setExpressionValue] = useState<string>(
    replaceCurlyBracesByParenthesis(formContext?.getValues(expressionFieldName))
  );

  const debouncedExpressionValue = useDebounce<string>(expressionValue, 300);

  const [isExpressionBuilderOpen, setIsExpressionBuilderOpen] =
    useState<boolean>(false);

  const handleExpressionValueChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    formContext?.setValue(expressionFieldName, event.target.value);
    setExpressionValue(replaceCurlyBracesByParenthesis(event.target.value));
  };

  const handleAddVariableClick = () =>
    setIsExpressionBuilderOpen(!isExpressionBuilderOpen);
  const isUtilizeHiddenComponentsEnabled = (): boolean => {
    return (
      (formContext.getValues(utilizeHiddenComponentsFieldName) as boolean) ??
      false
    );
  };

  const [uHCCheckbox, setuHCCheckbox] = useState<boolean>(
    isUtilizeHiddenComponentsEnabled()
  );

  const handleUtilizeHiddenComponentsChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const rules = formContext.getValues(
      rulesFieldName
    ) as ConditionalRuleConfig[];
    setuHCCheckbox(event.target.checked);
    formContext.setValue(
      utilizeHiddenComponentsFieldName,
      event.target.checked
    );

    if (!!event.target.value && !rules.length) {
      updateRules([getDefaultRuleConfig()]);
    } else {
      updateRules([]);
    }
  };

  const handleSelectedOption = (value: string) => {
    if (!value) return;

    const fieldValue = formContext?.getValues(expressionFieldName);
    const updatedValue = `${fieldValue}{${value}}`;

    formContext?.setValue(expressionFieldName, updatedValue);
    setExpressionValue(replaceCurlyBracesByParenthesis(updatedValue));
    setIsExpressionBuilderOpen(false);
    setTimeout(() => expressionFieldRef?.current?.focus(), 0);
  };

  const isExpressionValid = (expression: string) => {
    const variablesInFormula = expression
      ?.match(/{([^}]+)}/g)
      ?.map((res) => res.replace(/{|}/g, ''));

    const numericControls = getControlsFromWorkflow(
      pages,
      ControlKind.NUMERIC
    ).filter((ctrl) => {
      const ctrlProps = ctrl.schema?.properties as JSONSchema7Properties;
      const ctrlId = ctrlProps['id']?.default as string;

      return variablesInFormula?.includes(ctrlId);
    });

    return variablesInFormula?.length === numericControls.length;
  };

  const validateVariablesInExpression = (expression: string) => {
    const isExpresionValid = isExpressionValid(expression);
    formContext?.setValue(validExpressionFieldName, isExpresionValid);
  };

  const updateRules = useCallback((rules: ConditionalRuleConfig[]) => {
    formContext.setValue(rulesFieldName, rules);
  }, []);

  useEffect(() => {
    // subscribe to validate expresion on change
    const subscription = formContext?.watch((value, { name }) => {
      if (name === expressionFieldName) {
        validateVariablesInExpression(value[name] as string);
      }
    });

    // validate expression on load
    validateVariablesInExpression(formContext?.getValues(expressionFieldName));

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <Grid container spacing={3} sx={{ minWidth: '75vw' }}>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <InputTextField
              name={formatName(properties['label']?.$id as string)}
              label="Label"
              isDisabled={isReadOnlyView}
              fullWidth
            />
          </Grid>
          <Grid item md={4}>
            <InputTextField
              name={formatName(properties['id']?.$id as string)}
              label="ID"
              isDisabled={isReadOnlyView}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SizingSelect
              isReadOnlyView={isReadOnlyView}
              name={formatName(properties['gridSize']?.$id as string)}
            />
          </Grid>
        </Grid>
      </Grid>

      <InputTextField
        sx={{ display: 'none' }}
        name={validExpressionFieldName}
      />

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              '& input': {
                fontSize: '20px',
                fontFamily: 'monospace'
              }
            }}
          >
            <InputTextField
              inputProps={{
                ref: expressionFieldRef,
                fullWidth: true
              }}
              name={expressionFieldName}
              label="Expression Builder"
              isDisabled={isReadOnlyView}
              onChange={handleExpressionValueChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack sx={{ marginTop: '20px' }}>
              {debouncedExpressionValue && (
                <MathJax
                  renderMode="pre"
                  text={debouncedExpressionValue}
                  typesettingOptions={{ fn: 'asciimath2chtml' }}
                  dynamic={true}
                >
                  {debouncedExpressionValue}
                </MathJax>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={2}>
            <Button
              disabled={isReadOnlyView}
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddVariableClick}
              sx={{
                minHeight: '3rem',
                marginTop: '1rem'
              }}
              fullWidth
            >
              Add Field
            </Button>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack sx={{ marginTop: '16px' }}>
              <ExpressionBuilder
                label="Numeric Fields"
                options={getNumericFieldIds(pages)}
                isDisabled={isReadOnlyView}
                open={isExpressionBuilderOpen}
                onOpen={() => setIsExpressionBuilderOpen(true)}
                onClose={() => setIsExpressionBuilderOpen(false)}
                onSelectedOption={handleSelectedOption}
              />
            </Stack>
          </Grid>

          <Grid item>
            <InputCheckboxGroup
              name={`${formatName(
                properties['required']?.$id as string
              )}-inputCheckBoxGroup`}
              isDisabled={isReadOnlyView}
            >
              <InputCheckbox
                name={formatName(properties['required']?.$id as string)}
                defaultChecked={
                  (properties['required'].default as boolean) ?? false
                }
              >
                Required Field
              </InputCheckbox>
            </InputCheckboxGroup>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <InputCheckboxGroup
          name={`${utilizeHiddenComponentsFieldName}-inputCheckBoxGroup`}
          isDisabled={isReadOnlyView}
        >
          <InputCheckbox
            name={utilizeHiddenComponentsFieldName}
            onCheckboxChange={handleUtilizeHiddenComponentsChange}
            defaultChecked={uHCCheckbox}
          >
            Utilize Hidden Components
          </InputCheckbox>
        </InputCheckboxGroup>
      </Grid>
      <Grid item xs={12}>
        {uHCCheckbox && (
          <RulesEngine
            isReadOnlyView={isReadOnlyView}
            fieldName={rulesFieldName}
            updateRules={updateRules}
            fieldId={properties['id']?.default as string}
          />
        )}
        <InputTextField sx={{ display: 'none' }} name={rulesFieldName} />
      </Grid>
    </Grid>
  );
};
